import { useEffect, useCallback } from 'react'
import { UseFormWatch, UseFormSetValue, FieldValues } from 'react-hook-form'

interface UseFormPersistenceOptions<T extends FieldValues> {
  key: string // Clave única para el localStorage
  watch: UseFormWatch<T>
  setValue: UseFormSetValue<T>
  exclude?: (keyof T)[] // Campos a excluir del guardado
}

/**
 * Hook para persistir datos del formulario en localStorage
 * Guarda automáticamente cada cambio y recupera al cargar
 */
export function useFormPersistence<T extends FieldValues>({
  key,
  watch,
  setValue,
  exclude = [],
}: UseFormPersistenceOptions<T>) {
  
  // Recuperar datos guardados al montar el componente (solo una vez)
  useEffect(() => {
    // Solo ejecutar una vez al montar
    const savedData = localStorage.getItem(key)
    
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData)
        
        // Usar setTimeout para evitar actualizar durante el render
        setTimeout(() => {
          // Restaurar cada campo (excepto los excluidos)
          Object.keys(parsedData).forEach((fieldKey) => {
            if (!exclude.includes(fieldKey as keyof T)) {
              setValue(fieldKey as any, parsedData[fieldKey], { 
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true 
              })
            }
          })
          
          console.log(`✅ Datos recuperados de localStorage: ${key}`)
        }, 0)
      } catch (error) {
        console.error('Error al recuperar datos del localStorage:', error)
        localStorage.removeItem(key)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Array de dependencias vacío - solo ejecutar una vez al montar

  // Guardar datos en localStorage cada vez que cambian
  useEffect(() => {
    const subscription = watch((formData) => {
      try {
        // Filtrar campos excluidos
        const dataToSave: any = { ...formData }
        exclude.forEach((field) => {
          delete dataToSave[field as string]
        })
        
        localStorage.setItem(key, JSON.stringify(dataToSave))
      } catch (error) {
        console.error('Error al guardar en localStorage:', error)
      }
    })

    return () => subscription.unsubscribe()
  }, [key, watch, exclude])

  // Función para limpiar los datos guardados
  const clearSavedData = useCallback(() => {
    localStorage.removeItem(key)
    console.log(`🗑️ Datos eliminados de localStorage: ${key}`)
  }, [key])

  // Función para verificar si hay datos guardados
  const hasSavedData = useCallback(() => {
    return localStorage.getItem(key) !== null
  }, [key])

  return {
    clearSavedData,
    hasSavedData,
  }
}
